import React, { useMemo, useState } from "react";
import { Check, Loader2, Lock } from "lucide-react";
import Countdown from "../bond-wheel/Countdown";
import CoinTossStage from "./CoinTossStage";
import BondCoinFairPanel from "../bond-coin/BondCoinFairPanel";
import {
  COIN_POOL_FILL_MODE_LABEL,
  COIN_POOL_STATUS_LABEL,
  COIN_SIDE_LABEL,
  canPlaceCoinPoolOrder,
  coinPoolStatusTone,
  formatCoinPoolCoin,
  makeCoinPoolJoinIdempotencyKey,
  poolSharePercent,
} from "../../utils/coinPoolHelpers";

const BondCoinPoolStage = ({
  round,
  constants,
  loading,
  actionLoading,
  isAuthenticated,
  userCoin,
  onPlaceOrder,
  onSignIn,
}) => {
  const [selectedSide, setSelectedSide] = useState("head");
  const [stakeInput, setStakeInput] = useState("");
  const [fillMode, setFillMode] = useState("partial");

  const minStake = constants?.minStakeCoin ?? 100;
  const status = round?.status || "waiting";
  const tone = coinPoolStatusTone(status);
  const canOrder = canPlaceCoinPoolOrder(round);
  const isTerminal = status === "complete" || status === "canceled";
  const spinning = status === "locked" || status === "settling";

  const stakeNum = Math.floor(Number(stakeInput) || 0);
  const stakeValid = stakeNum >= minStake && stakeNum <= userCoin;

  const matchedHead = Math.max(0, Number(round?.headStaked) || 0);
  const matchedTail = Math.max(0, Number(round?.tailStaked) || 0);
  const waitingHead = Math.max(0, Number(round?.book?.openHeadTotal) || 0);
  const waitingTail = Math.max(0, Number(round?.book?.openTailTotal) || 0);

  const oppositeSide = selectedSide === "head" ? "tail" : "head";
  const oppositeWaiting = oppositeSide === "head" ? waitingHead : waitingTail;

  const quickStakes = useMemo(() => {
    const base = [minStake, minStake * 5, minStake * 10, minStake * 20];
    return [...new Set(base)].filter((n) => n <= userCoin).slice(0, 4);
  }, [minStake, userCoin]);

  const projectedShare = poolSharePercent(
    stakeNum,
    (selectedSide === "head" ? matchedHead : matchedTail) + stakeNum
  );

  const handlePlaceOrder = async () => {
    if (!stakeValid || actionLoading) return;
    const ok = await onPlaceOrder?.({
      side: selectedSide,
      amount: stakeNum,
      fillMode,
      joinIdempotencyKey: makeCoinPoolJoinIdempotencyKey(),
    });
    if (ok) setStakeInput("");
  };

  if (loading && !round) {
    return (
      <div className="bplay-wheel-loading">
        <Loader2 size={28} className="bplay-spin" />
      </div>
    );
  }

  return (
    <div className={`bcoin-stage${isTerminal ? " bcoin-stage-terminal" : ""}`}>
      <header className="bcoin-head">
        <div>
          <h1 className="bcoin-title">Coin Pool</h1>
        </div>
        <span className={`bcoin-status bcoin-status-${tone}`}>
          {COIN_POOL_STATUS_LABEL[status] || status}
        </span>
      </header>

      <CoinTossStage outcome={round?.outcome} spinning={spinning} locked={status === "locked"} />

      {round?.outcome && isTerminal ? (
        <p className="bcoin-round-outcome">
          <strong>{COIN_SIDE_LABEL[round.outcome] || round.outcome}</strong>
        </p>
      ) : (
        <p className="bcoin-round-outcome bcoin-round-outcome-placeholder" aria-hidden>
          &nbsp;
        </p>
      )}

      {round?.seedHash ? <BondCoinFairPanel round={round} game="pool" compact /> : null}

      {status === "waiting" && round?.waitingEndsAt ? (
        <p className="bcoin-countdown-label">
          Closes in <Countdown deadline={round.waitingEndsAt} tone="live" />
        </p>
      ) : null}

      {status === "locked" && round?.tossAt ? (
        <p className="bcoin-countdown-label">
          Toss in <Countdown deadline={round.tossAt} tone="live" />
        </p>
      ) : null}

      {!canOrder && !isTerminal ? (
        <p className="bcoin-ob-closed-banner">
          Book closed.
        </p>
      ) : null}

      {canOrder && !isAuthenticated ? (
        <div className="bcoin-ob-signin-banner">
          <Lock size={18} strokeWidth={2} aria-hidden />
          <div>
            <p>Sign in to place orders.</p>
          </div>
          <button type="button" className="bcoin-btn bcoin-btn-primary" onClick={onSignIn}>
            Sign in
          </button>
        </div>
      ) : null}

      {canOrder && isAuthenticated ? (
        <div className="bcoin-actions bcoin-ob-form">
          <div className="bcoin-side-toggle" role="tablist" aria-label="Pick a side">
            {["head", "tail"].map((side) => (
              <button
                key={side}
                type="button"
                role="tab"
                aria-selected={selectedSide === side}
                className={`bcoin-side-btn ${selectedSide === side ? "active" : ""} ${side}`}
                onClick={() => setSelectedSide(side)}
              >
                {COIN_SIDE_LABEL[side]}
              </button>
            ))}
          </div>

          <div className="bcoin-ob-fill-field">
            <span className="bcoin-ob-fill-label">Fill</span>
            <div className="bcoin-ob-fill-options" role="radiogroup" aria-label="Fill mode">
              {["partial", "exact"].map((mode) => {
                const selected = fillMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    className={`bcoin-ob-fill-option${selected ? " active" : ""}`}
                    onClick={() => setFillMode(mode)}
                  >
                    <span className="bcoin-ob-fill-box" aria-hidden>
                      {selected ? <Check size={11} strokeWidth={3} /> : null}
                    </span>
                    <span className="bcoin-ob-fill-copy">
                      <strong>{COIN_POOL_FILL_MODE_LABEL[mode]}</strong>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bcoin-join-row bcoin-pool-join-row">
            <label className="bcoin-stake-field">
              <span>Amount (min {formatCoinPoolCoin(minStake)})</span>
              <input
                type="number"
                min={minStake}
                step={50}
                value={stakeInput}
                onChange={(e) => setStakeInput(e.target.value)}
                placeholder={String(minStake)}
              />
            </label>
            <button
              type="button"
              className="bcoin-btn bcoin-btn-primary bcoin-pool-buy"
              disabled={actionLoading || !stakeValid}
              onClick={handlePlaceOrder}
            >
              {actionLoading ? <Loader2 size={16} className="bplay-spin" /> : "Submit"}
            </button>
          </div>

          {quickStakes.length > 0 ? (
            <div className="bcoin-ob-quick-stakes">
              {quickStakes.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`bcoin-ob-quick-chip${stakeNum === n ? " active" : ""}`}
                  onClick={() => setStakeInput(String(n))}
                >
                  {formatCoinPoolCoin(n)}
                </button>
              ))}
            </div>
          ) : null}

          {stakeValid ? (
            <p className="bcoin-share-preview">
              ≈ <strong>{projectedShare}%</strong> pool share
              {oppositeWaiting > 0 ? (
                <>
                  {" "}
                  · <span className="bcoin-ob-depth-hint">{formatCoinPoolCoin(oppositeWaiting)} on {COIN_SIDE_LABEL[oppositeSide]}</span>
                </>
              ) : null}
            </p>
          ) : stakeInput && !stakeValid ? (
            <p className="bcoin-share-preview bcoin-ob-field-error">
              {stakeNum > userCoin ? "Insufficient balance" : `Min ${formatCoinPoolCoin(minStake)} COIN`}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default BondCoinPoolStage;
